import React, { useState, useRef, useEffect, useContext } from "react";
import { useNavigate, useLocation, Link as RouterLink } from "react-router-dom";
import { emailSignIn, getMyProfile, phoneSignIn } from "../../services/auth";
import { ToastContainer, toast } from "react-toastify";
import AuthContext from "../Context/AuthProvider";
import image from "../../assets/images/signin.jpg";
import { intialFilterState } from "../../services/profile";
import { filterExpander } from "./../common/utilities/FilterTranslator";
import { countryCodes } from "../constants/Constants";
import InactivityHelpModal from "../common/InactivityHelpModal";

export default function SignInSide() {
  const {
    setAuth,
    setProfile,
    addFavouritesToState,
    setUserRole,
    setIsUserPaymentValid,
    addReportsToState,
    loadFilters,
    setProfileCompleteReminderTime,
    setUserSignedTncVersion,
    setUserSignedDeclarationTime,
    setUserViewedPreviewIntroTime
  } = useContext(AuthContext);

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/home";

  const [authType, setAuthType] = useState("phone");
  const [countryCode, setCountryCode] = useState("+91");
  const [usernameError, setUsernameError] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [search, setSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showSignupBanner, setShowSignupBanner] = useState(false);
  const [wrongPasswordCount, setWrongPasswordCount] = useState(0); 
  const [resetTrigger, setResetTrigger] = useState(0); 
  const updateResetTrigger = () => setResetTrigger(prev => prev +1);

  const dropdownRef = useRef(null);

  const usernameValidation = (username) => {
    if (username.length === 0) {
      setUsernameError("");
      return;
    }
    if (authType === "email") {
      const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;
      if (emailRegex.test(username)) {
        setUsernameError("");
      } else {
        setUsernameError("Invalid Email");
      }
    } else {
      if (countryCode === "+91") {
        // India: 10 digits, not starting with 0
        const phoneRegex = /^[1-9][0-9]{9}$/;
        if (phoneRegex.test(username)) {
          setUsernameError("");
        } else {
          setUsernameError("Enter a valid 10-digit Indian phone number (without country code)");
        }
      } else {
        // Other countries: 6-15 digits, not starting with 0
        const phoneRegex = /^[1-9][0-9]{5,14}$/;
        if (phoneRegex.test(username)) {
          setUsernameError("");
        } else {
          setUsernameError("Enter a valid phone number (6-15 digits, without country code)");
        }
      }
    }
  };

  const passwordValidation = (password) => {
    if (password.length < 1) {
      setPasswordError(true);
      return false;
    } else {
      setPasswordError(false);
      return true;
    }
  };

  useEffect(() => {
    usernameValidation(username);
  }, [authType]);

  const handleSubmit = async (event) => {
    updateResetTrigger();
    event.preventDefault();
    if(username.length === 0 || password.length === 0) {
      toast.info("Please enter valid credentials", {
        timeOut: 1000,
      });
      return;
    }
    usernameValidation(username);
    passwordValidation(password);
    if (passwordError || usernameError) {
      toast.info("Invalid credentials", {
        timeOut: 1000,
      });
      return;
    }
    let token = null;
    if (authType === "email") {
      let response = await emailSignIn(username, password);
      if (response.data.errors) {
        toast.info("Email Id or Password is incorrect", {
          timeOut: 1000,
        });
        return;
      } else {
        token = response.data.data.emailSignin;
      }
    } else {
      let response = await phoneSignIn(`${countryCode}${username}`, password);
      if (response.data.errors) {
        const errorMsg = response.data?.errors[0]?.message?.toLowerCase() ?? "";
      
        if (errorMsg.includes("user not found")) {
          toast.info("Account not found. Please sign up before signing in.", {
            timeOut: 5000,
          });
          setShowSignupBanner(true);
          const signUpBtn = document.getElementById("sign-up-btn");
          if (signUpBtn) {
            signUpBtn.classList.add("animate-pulse", "font-bold");
            setTimeout(() => {
              signUpBtn.classList.remove("animate-pulse", "font-bold");
            }, 5000);
          }
          setWrongPasswordCount(0);
          return;
        } else if (errorMsg.includes("authentication failed")) {
          const forgotPasswordBtn = document.getElementById("forgotpassword-up-btn");

          if (forgotPasswordBtn && wrongPasswordCount > 0) {
            forgotPasswordBtn.classList.add("animate-pulse", "font-bold");
            setTimeout(() => {
              forgotPasswordBtn.classList.remove("animate-pulse", "font-bold");
            }, 5000);
          }
          setWrongPasswordCount((prev) => prev + 1);

          toast.error("Incorrect password. Please check the password.", {
            timeOut: 5000,
          });
          setShowSignupBanner(false);
          return;
        } else {
          setShowSignupBanner(false);
          setWrongPasswordCount(0);
          toast.error("Something went wrong. Please try again later or reach out to support.", {
            timeOut: 5000,
          });
          return;
        }
      }  else {
        token = response.data.data.phoneSignin;
      }
    }
    let getProfile = await getMyProfile(token);
    
    if (getProfile.errors) {
      console.error("failure in getting my profile", getProfile.errors);
    } else {
      setAuth({
        token,
        authenticationByEmail: authType === "email",
        authenticationUsername: username,
      });
      let isUserPaymentValid =
        parseFloat(getProfile.data.getMe.user.remainingValidityMillis) > 0;
      isUserPaymentValid && setIsUserPaymentValid(true);
      setProfileCompleteReminderTime(getProfile.data.getMe.user.profileReminderTime)

      let myProfile = getProfile.data.getMe.profile;
      let roles = getProfile.data.getMe.roles;
      
      if (roles.length > 0 && roles[0] === "admin") setUserRole("admin");
      let favourites = getProfile.data.getMe.favouriteIds;
      addFavouritesToState(favourites);
      let reports = getProfile.data.getMe.reportedUsers;
      addReportsToState(reports);
      let filterPreferenceString = getProfile.data.getMe.user.filterPreferences;
      const parsedFilterState = filterPreferenceString
        ? JSON.parse(filterPreferenceString)
        : {};
      var filterState = filterExpander(
        { ...intialFilterState() },
        parsedFilterState
      );
      setUserSignedTncVersion(getProfile.data.getMe.acknowledgements.lastTnCVersion);
      setUserSignedDeclarationTime(getProfile.data.getMe.acknowledgements.lastDeclarationTime);
      setUserViewedPreviewIntroTime(getProfile.data.getMe.acknowledgements.lastPreviewTime);
      loadFilters(filterState);
      setProfile(myProfile);
    }
    navigate(from, { replace: true });
  };

  const filteredCountryCodes = countryCodes.filter(
    (code) =>
      code.country.toLowerCase().includes(search.toLowerCase()) ||
      code.code.includes(search)
  );

  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <div
      className="flex items-center justify-center min-h-screen bg-cover px-4 sm:px-6 overflow-y-auto"
      style={{
        backgroundImage: `url(${image})`,
      }}
    >
      <div className="flex flex-col justify-center w-full max-w-md bg-white p-8 sm:max-h-[90%] rounded-md shadow-2xl">
        <ToastContainer />
        <InactivityHelpModal resetTrigger={resetTrigger}/>
        <div className="flex flex-col items-center mb-2">
          <h1 className="text-2xl font-bold">SIGN IN</h1>
        </div>
        {showSignupBanner && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-300 text-blue-800 text-sm px-3 py-2 rounded-md text-center">
            We couldn’t find your account. Please{" "}
            <RouterLink
              to="/signup"
              className="font-semibold underline hover:text-blue-600"
              id="sign-up-link"
            >
              Sign Up
            </RouterLink>{" "}
            before signing in.
          </div>
        )}
        {wrongPasswordCount > 1 && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-300 text-blue-800 text-sm px-3 py-2 rounded-md text-center">
           Forgot your password? Please reset your password{" "}
            <RouterLink
              to="/forgot-password"
              className="font-semibold underline hover:text-blue-600"
            >
              here
            </RouterLink>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="authType"
              className="block text-sm font-medium text-gray-500"
            >
              Sign In With
            </label>
            <select
              id="authType"
              value={authType}
              onChange={(e) => {
                updateResetTrigger()
                setAuthType(e.target.value)
              }}
              className="select select-bordered w-full bg-white"
            >
              <option value="email">Email</option>
              <option value="phone">Phone</option>
            </select>
          </div>
          {authType === "phone" && (
            <div>
              <label
                htmlFor="countryCode"
                className="block text-sm font-medium text-gray-500"
              >
                Country Code
              </label>
              <div className="relative" ref={dropdownRef}>
                <button
                  type="button"
                  onClick={() => {
                    updateResetTrigger()
                    setIsDropdownOpen(!isDropdownOpen)
                  }}
                  className="select select-bordered w-full bg-white"
                >
                  <p className="my-auto">
                    {countryCode} (
                    {
                      countryCodes.find((code) => code.code === countryCode)
                        .country
                    }
                    )
                  </p>
                </button>

                {isDropdownOpen && (
                  <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md overflow-auto">
                    <input
                      type="text"
                      className="input input-bordered w-full px-2 py-1 bg-white text-black"
                      placeholder="Search country or code"
                      value={search}
                      onChange={(e) => {
                        updateResetTrigger()
                        setSearch(e.target.value)
                      }}
                    />
                    <ul className="py-1">
                      {filteredCountryCodes.map((code) => (
                        <li
                          key={code.code}
                          className="cursor-pointer hover:bg-gray-100 px-2 py-1"
                          onClick={() => {
                            updateResetTrigger()
                            setCountryCode(code.code);
                            setIsDropdownOpen(false);
                          }}
                        >
                          {code.code} ({code.country})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
          <div>
            <label
              htmlFor="username"
              className="block text-sm font-medium text-gray-500"
            >
              {authType === "email" ? "Email Address" : "Phone Number"}
            </label>
            <input
              type="text"
              name="username"
              id="username"
              value={username}
              onChange={(e) => {
                updateResetTrigger()
                setUsername(e.target.value);
                usernameValidation(e.target.value);
              }}
              className={`${
                usernameError ? "border-red-500" : "border-gray-300"
              } input input-bordered input-primary bg-white focus:outline-none focus:border-1 w-full`}
              placeholder={`Enter your ${
                authType === "email" ? "email address" : "phone number"
              }`}
            />
            {usernameError && (
              <p className="text-red-500 text-xs mt-1">{usernameError}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="password"
              className="block text-sm font-medium text-gray-500"
            >
              Password
            </label>
            <input
              type="password"
              name="password"
              id="password"
              value={password}
              onChange={(e) => {
                updateResetTrigger()
                setPassword(e.target.value);
                passwordValidation(e.target.value);
              }}
              className={`${
                passwordError ? "border-red-500" : "border-gray-300"
              } input input-bordered input-primary bg-white focus:outline-none focus:border-1 w-full`}
              placeholder="Enter your password"
            />
            {passwordError && (
              <p className="text-red-500 text-xs mt-1">Enter password</p>
            )}
          </div>
          <div className="mt-4">
            <button
              type="submit"
              className="btn btn-primary text-white font-bold w-full rounded-md mt-2"
            >
              Sign In
            </button>
          </div>
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
            <RouterLink
              to="/forgot-password"
              id="forgotpassword-up-btn"
              className="text-sm text-blue-600 hover:text-blue-900"
            >
              Forgot Password?
            </RouterLink>
            <RouterLink
              to="/signup"
              id="sign-up-btn"
              className="text-sm text-blue-600 hover:text-blue-900 transition-colors"
            >
              {"Don't have an account? Sign Up"}
            </RouterLink>
          </div>
        </form>
      </div>
    </div>
  );
}
